import "./tailwind.css";
import { SearchWrapper } from "./SearchWrapper";
import type { ReactElement, ReactNode } from "react";
import React, { useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from "react";
import { IntlProvider } from 'react-intl';
import { defaultLocale } from '../i18n/supported-locales';
import { Messages } from '../types';
import { loadLocaleData } from '../utils/routing/url-helpers';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';
import { syncHtmlLangAttribute } from '../utils/routing/sync-html-lang-attribute';
import { buildFooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { FooterStringsProvider } from '../contexts/FooterStringsContext';
import { usePageContext } from 'vike-react/usePageContext';
import { useData } from 'vike-react/useData';
import type { PageContextClient } from 'vike/types';
import SavedStateContext from '../contexts/SavedStateContext';
import { SearchProvider } from '../contexts/SearchContext';
import { getIndexName } from '../constants';
import { useSentryUser } from '../hooks/useSentryUser';
import { useProfilePreferencesSettingsSync } from '../hooks/useProfilePreferencesSettingsSync';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { LayoutPageErrorFallback } from '../components/LayoutPageErrorFallback';
import { NavigationProvider, useNavigationOptional } from '../contexts/NavigationContext';
import { NavigationProgress } from '../components/NavigationProgress';
import { ToastProvider } from '../contexts/ToastContext';
import { ToastContainer } from '../components/Toast';
import { LocaleMismatchPrompt } from '../components/LocaleMismatchPrompt';
import { SRPShell } from '../pages/index/static/SRPShell';
import type { SessionUser } from '../types/auth';
import type { StaticSRPData } from '../types/srp-static';
import { SrpView } from '../features/srp/SrpView';
import type { SrpViewModel } from '../features/srp/SrpViewModel';
import { serializeForHtmlScript } from '../features/srp/serializeForHtmlScript';
import { parseSrpInitialScript } from '../features/srp/parseSrpInitialScript';
import { useSrpIsland } from '../contexts/SrpIslandContext';
import { AuthProvider, useAuthOptional } from '../contexts/AuthContext';
import { FeedbackModalProvider } from '../contexts/FeedbackModalContext';
import { isToolsPage } from '../utils/routing/route-utils';
import { isSearchPagePathname } from '../utils/routing/is-search-page-pathname';
import { isLayoutClientRouteSyncedWithWindow } from '../utils/routing/layout-client-route-synced';
import { layoutPageDataLacksSrpPayload } from '../utils/routing/merge-layout-srp-page-data';
import { useIntl } from 'react-intl';
import { MinimalChrome } from './MinimalChrome';
import { Provider } from 'react-redux';
import { createStore } from '../redux/createStore';
import JobDetailPage from '../pages/job/@jobId/@seoSlug/+Page';
import type { Data as JobData } from '../pages/job/@jobId/@seoSlug/+data';
import { getJobListingHttpStatusCode } from '../utils/job-detail/get-job-listing-http-status-code';
import { fetchSimilarJobsAndLocationCountFromAlgolia } from '../utils/job-detail/similar-jobs-algolia';
import { parseJobInitialScriptFromDocument } from '../utils/job-detail/parse-job-initial-script';
import { ENV_CONFIG } from '../config/environment';
import { CookieConsentBanner } from '../components/CookieConsentBanner';
import { JOB_ISLAND_SWAPPED_EVENT } from '../pages/job/job-island-events';
import { InspectorMount } from '../i18n-review/adapters/InspectorMount';

const JOB_PATH_WITH_LOCALE = /^\/(?:en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)(?:\/|$)/i;
const JOB_PATH_NO_LOCALE = /^\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)(?:\/|$)/i;

function getJobIdsFromPathname(pathname: string): { jobId: string; companyId: string } | null {
  const path = String(pathname || '');
  let match = path.match(JOB_PATH_WITH_LOCALE);
  if (match) {
    const [, , jobId, companyId] = match;
    return jobId && companyId ? { jobId, companyId } : null;
  }
  match = path.match(JOB_PATH_NO_LOCALE);
  if (match) {
    const [, , jobId, companyId] = match;
    return jobId && companyId ? { jobId, companyId } : null;
  }
  return null;
}

/** Renders the job detail shell; uses useData() when available, with client-side fetch fallback for navigation. */
export function JobRouteContent({ footerStrings }: { footerStrings: Record<string, string> }) {
  const dataFromHook = useData<JobData>();
  /** After island swap, drop SSR shell from the React tree (client navigations). First load uses DOM `remove()` in job.client.ts. */
  const [jobStaticShellRemoved, setJobStaticShellRemoved] = useState(false);
  const [clientData, setClientData] = useState<JobData | null>(null);
  /** Synchronous read of `#job-initial` on first client frame so hydration matches SSR (see parseJobInitialScriptFromDocument). */
  const [scriptData, setScriptData] = useState<JobData | null>(() =>
    typeof document !== 'undefined' ? parseJobInitialScriptFromDocument(document) : null,
  );
  const pageContext = usePageContext();
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const jobLocale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || defaultLocale;

  // Re-sync after pathname changes (client navigation) or if DOM script updates.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parsedData = parseJobInitialScriptFromDocument(document);
    if (parsedData?.job) {
      setScriptData(parsedData);
    }
  }, [pathname]);

  const baseData = dataFromHook ?? scriptData;

  // Client-side fallback: when navigating to job URL, useData() may not have run yet; fetch job then.
  // Relative URL: in dev Vite proxies /api/getjob to www.jobcrawls.com (avoids CORS); in prod same-origin.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasHydratedSimilarJobs =
      !!baseData?.job &&
      Array.isArray(baseData?.similarJobs) &&
      baseData.similarJobs.length > 0;
    if (hasHydratedSimilarJobs) return;
    const ids = getJobIdsFromPathname(pathname);
    if (!ids) return;
    const { jobId, companyId } = ids;
    let cancelled = false;

    const resolveJobBody = async (): Promise<{ job: Record<string, unknown> } | null> => {
      if (baseData?.job) {
        return { job: baseData.job as unknown as Record<string, unknown> };
      }
      const url = `/api/getjob?pk=${encodeURIComponent(jobId)}&sk=${encodeURIComponent(companyId)}`;
      const res = await fetch(url, { headers: { 'user-agent': 'jobcrawls-internal/get-job' } });
      if (!res.ok) return null;
      return (await res.json()) as { job: Record<string, unknown> } | null;
    };

    resolveJobBody()
      .then(async (body) => {
        if (cancelled || !body?.job) return;
        const job = body.job;
        const slugify = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const seoSlug = job.seoSlug || slugify(`${job.title || ''}-${job.companyId || job.company || ''}-${Array.isArray(job.location) ? job.location[0] : job.location || ''}`);
        const jl = (['en', 'fi', 'sv'].includes(jobLocale) ? jobLocale : 'en') as 'en' | 'fi' | 'sv';
        const primaryLocation = Array.isArray(job.location) ? job.location[0] : job.location || '';
        let similarJobs: JobData['similarJobs'] = [];
        let locationJobCount: JobData['locationJobCount'] = null;
        try {
          const { similarJobs: sj, locationJobCount: lc } = await fetchSimilarJobsAndLocationCountFromAlgolia({
            jobObjectId: String((job as { objectID?: string }).objectID ?? jobId),
            normalisedTitle: (job as { normalisedTitle?: string }).normalisedTitle,
            primaryLocation: primaryLocation || '',
            jobLocale: jl,
            indexName: getIndexName(jl),
            baseUrl: ENV_CONFIG.URLS.PROD_URL || '',
          });
          similarJobs = sj;
          locationJobCount = lc;
        } catch {
          /* Algolia optional; cards stay empty */
        }
        if (cancelled) return;
        setClientData({
          job,
          seoSlug,
          metaTitle: '',
          metaDescription: '',
          httpStatusCode: getJobListingHttpStatusCode(job, Date.now()),
          heroImage: undefined,
          heroImageHasTransparency: false,
          hasAnyCharts: false,
          similarJobs,
          locationJobCount,
        } as JobData);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname, baseData]);

  const jobData = useMemo(() => {
    const sourceData = dataFromHook ?? scriptData;
    if (!sourceData) return clientData;
    if (!clientData) return sourceData;

    const hookHasSimilarJobs =
      Array.isArray(sourceData.similarJobs) && sourceData.similarJobs.length > 0;
    const clientHasSimilarJobs =
      Array.isArray(clientData.similarJobs) && clientData.similarJobs.length > 0;
    const shouldUseClientSimilarJobs = !hookHasSimilarJobs && clientHasSimilarJobs;

    return {
      ...sourceData,
      similarJobs: shouldUseClientSimilarJobs ? clientData.similarJobs : sourceData.similarJobs,
      locationJobCount:
        sourceData.locationJobCount ?? clientData.locationJobCount ?? null,
    };
  }, [dataFromHook, scriptData, clientData]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hookCount = Array.isArray(dataFromHook?.similarJobs) ? dataFromHook.similarJobs.length : -1;
    const scriptCount = Array.isArray(scriptData?.similarJobs) ? scriptData.similarJobs.length : -1;
    const clientCount = Array.isArray(clientData?.similarJobs) ? clientData.similarJobs.length : -1;
    const mergedCount = Array.isArray(jobData?.similarJobs) ? jobData.similarJobs.length : -1;
    const objectId = (jobData as { job?: { objectID?: string } } | null)?.job?.objectID ?? 'none';
    console.log('[SimilarJobsDebug][LayoutDefault] merge-state', {
      objectId,
      hookCount,
      scriptCount,
      clientCount,
      mergedCount,
      hydrationKey: `${objectId}:${mergedCount}:${(jobData as JobData | null)?.locationJobCount ?? 'null'}`,
    });
  }, [dataFromHook, scriptData, clientData, jobData]);
  const hasJobData = !!(jobData?.job);
  const jobHydrationKey = useMemo(() => {
    const objectId = (jobData as { job?: { objectID?: string } } | null)?.job?.objectID ?? '';
    const similarCount = Array.isArray((jobData as JobData | null)?.similarJobs)
      ? ((jobData as JobData).similarJobs?.length ?? 0)
      : 0;
    const locationCount = (jobData as JobData | null)?.locationJobCount ?? 'null';
    return `${objectId}:${similarCount}:${locationCount}`;
  }, [jobData]);
  const jobStore = useMemo(
    () => createStore({ ...pageContext, data: jobData ?? null, isClientSide: false } as any),
    [(jobData as { job?: { objectID?: string } } | null)?.job?.objectID]
  );

  // When job data is available (e.g. after client fetch on nav), run island loader so #job-app becomes interactive.
  // Dynamic import to avoid pulling client-only job.client.ts into the SSR bundle.
  useEffect(() => {
    if (typeof window === 'undefined' || !hasJobData) return;
    const appEl = document.getElementById('job-app');
    const scriptEl = document.getElementById('job-initial');
    if (appEl && scriptEl?.tagName === 'SCRIPT') {
      const pageContextUserState =
        ((pageContext as { userState?: Record<string, unknown> })?.userState &&
        typeof (pageContext as { userState?: Record<string, unknown> }).userState === 'object')
          ? ((pageContext as { userState?: Record<string, unknown> }).userState as Record<string, unknown>)
          : null;
      // Important: do not pass empty {} override, otherwise job.client can't fall back to
      // userState parsed from #job-initial (SSR payload), which can cause saved-state paint drift.
      const payloadOverride = pageContextUserState && Object.keys(pageContextUserState).length > 0
        ? {
            jobData: jobData as JobData,
            userState: pageContextUserState,
          }
        : {
            jobData: jobData as JobData,
          };
      import('../pages/job/job.client').then((m) =>
        console.log('[SimilarJobsDebug][LayoutDefault] runJobIslandLoader payload', {
          objectId: (jobData as { job?: { objectID?: string } } | null)?.job?.objectID ?? 'none',
          similarCount: Array.isArray((jobData as JobData | null)?.similarJobs) ? ((jobData as JobData).similarJobs?.length ?? 0) : -1,
          hasScriptTag: !!scriptEl,
          userStateSource: payloadOverride && 'userState' in payloadOverride ? 'pageContext' : 'script',
        }) ||
        m.runJobIslandLoader(pageContext as PageContextClient, payloadOverride),
      );
    }
  }, [hasJobData, pageContext, jobHydrationKey, jobData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onSwapped = () => setJobStaticShellRemoved(true);
    window.addEventListener(JOB_ISLAND_SWAPPED_EVENT, onSwapped);
    return () => window.removeEventListener(JOB_ISLAND_SWAPPED_EVENT, onSwapped);
  }, []);

  return (
    <>
      <div id="job-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
        {!jobStaticShellRemoved ? (
          <div id="job-ssr">
            <FooterStringsProvider strings={footerStrings}>
              <Provider store={jobStore}>
                <MinimalChrome>
                  {hasJobData ? (
                    <JobDetailPage initialData={jobData as any} initialLocale={jobLocale} />
                  ) : (
                    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#170F07]" />
                  )}
                </MinimalChrome>
              </Provider>
            </FooterStringsProvider>
          </div>
        ) : null}
        <div
          id="job-app"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            visibility: 'hidden',
            // Until the island swap, do not block taps/scroll on #job-ssr (e.g. user menu portaled to body can still hit this layer in some browsers).
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        />
      </div>
      {hasJobData && (
        <script
          id="job-initial"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              data: jobData,
              userState: (pageContext as { userState?: Record<string, unknown> })?.userState ?? {},
            }).replace(/</g, '\\u003c'),
          }}
        />
      )}
    </>
  );
}

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

/** Path segments (without locale) that use the minimal layout (nav + content, no SearchProvider/Redux). */
const MINIMAL_ROUTE_SEGMENTS = new Set([
  'resources', 'tyoelama', 'arbetsliv',
  'about', 'tietoa-jobcrawlsista', 'om-jobcrawls',
  'for-employers', 'tyonantajille', 'for-arbetsgivare',
  'locations', 'sijainnit', 'platser',
  'authors',
]);

/** Sub-routes that need full layout (SearchWrapper) even when under resources. */
const FULL_LAYOUT_SUBROUTES = new Set<string>([]);

function isMinimalRoute(path: string): boolean {
  const normalized = String(path).replace(/\/+$/, '') || '/';
  const segments = String(path).split('/').filter(Boolean);
  if (segments.some((s) => FULL_LAYOUT_SUBROUTES.has(s.toLowerCase()))) return false;
  const first = segments[0]?.toLowerCase();
  const second = segments[1]?.toLowerCase();
  if (first && MINIMAL_ROUTE_SEGMENTS.has(first)) return true;
  if (first && ['en', 'fi', 'sv'].includes(first) && second && MINIMAL_ROUTE_SEGMENTS.has(second)) return true;
  return false;
}

/** Get locale from pathname (first segment if en/fi/sv). Client-side fallback when pageContext.locale isn't ready during hydration. */
function getLocaleFromPathname(pathname: string): (typeof SUPPORTED_LOCALES)[number] | null {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? (first as (typeof SUPPORTED_LOCALES)[number]) : null;
}

// Utility to determine if this is the search page (homepage for the locale)
function isSearchPageVike(pageContext: { urlLogical?: string; urlPathname?: string; urlParsed?: { pathname?: string } } | null | undefined) {
  // Prefer urlPathname when available (vike provides it), else urlLogical. On client during hydration,
  // pageContext may not be ready yet—fall back to window.location.pathname to avoid showing SRP navbar on resource pages.
  const path =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (pageContext as any)?.urlParsed?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  return isSearchPagePathname(path);
}

// Wrapper to memoize the arrays before passing to the provider
// Prevents array recreation on every render
function SavedStateContextProviderMemoizedMemo({
  children,
  savedJobIds,
  appliedJobIds,
  viewedJobIds,
}: {
  children: ReactNode;
  savedJobIds?: string[];
  appliedJobIds?: string[];
  viewedJobIds?: string[];
}) {
  // Memoize arrays to prevent recreation if pageContext.data changes by reference
  const memoizedSavedJobIds = useMemo(() => savedJobIds || [], [savedJobIds?.join(',')]);
  const memoizedAppliedJobIds = useMemo(() => appliedJobIds || [], [appliedJobIds?.join(',')]);
  const memoizedViewedJobIds = useMemo(() => viewedJobIds || [], [viewedJobIds?.join(',')]);
  
  return (
    <SavedStateContextProviderMemoized
      savedJobIds={memoizedSavedJobIds}
      appliedJobIds={memoizedAppliedJobIds}
      viewedJobIds={memoizedViewedJobIds}
    >
      {children}
    </SavedStateContextProviderMemoized>
  );
}

/** Wrapper that calls useProfilePreferencesSettingsSync. Renders null. Used so we can catch Redux Provider missing via ErrorBoundary. */
function ProfilePreferencesSettingsSyncGuard({ userId }: { userId: string | null | undefined }) {
  useProfilePreferencesSettingsSync(userId ?? null);
  return null;
}

/** Resolves userId for sync: use Auth context when available (e.g. after client session fetch), else pageContext from SSR. Calls hooks unconditionally so order stays stable across routes. */
function useEffectiveUserId(): string | null {
  const auth = useAuthOptional();
  const pageContext = usePageContext();
  if (auth?.user?.id) return auth.user.id;
  return (pageContext as { user?: { id?: string } })?.user?.id ?? null;
}

// Memoized SavedStateContext provider to prevent unnecessary re-renders
// Only recreates Sets when the actual IDs change
function SavedStateContextProviderMemoized({ 
  children, 
  savedJobIds, 
  appliedJobIds, 
  viewedJobIds 
}: { 
  children: ReactNode;
  savedJobIds: string[];
  appliedJobIds: string[];
  viewedJobIds: string[];
}) {
  // Create stable string keys for dependency tracking (sorted for consistency, order shouldn't matter for Sets)
  const savedKey = JSON.stringify([...savedJobIds].sort());
  const appliedKey = JSON.stringify([...appliedJobIds].sort());
  const viewedKey = JSON.stringify([...viewedJobIds].sort());
  
  const savedStateValue = useMemo(() => ({
    saved: new Set<string>(savedJobIds),
    applied: new Set<string>(appliedJobIds),
    viewed: new Set<string>(viewedJobIds),
  }), [savedKey, appliedKey, viewedKey]);
  
  return (
    <SavedStateContext.Provider value={savedStateValue}>
      {children}
    </SavedStateContext.Provider>
  );
}

function LayoutContent({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const effectiveUserId = useEffectiveUserId();
  const pathnameForLocale =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = resolvePageLocale({
    locale: pageContext?.locale as string | undefined,
    urlPathname: pathnameForLocale,
  });
  // Memoize messages and footer strings (SSR-computed; footer never uses react-intl)
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const footerStrings = useMemo(() => buildFooterStaticStrings(messages), [messages]);
  
  // Navigation context (optional so hook order is stable when outside provider)
  const navigation = useNavigationOptional();
  
  // Setup Sentry user context
  useSentryUser();
  
  // Track initial load to prevent scroll/navigation on SSR hydration
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousPathname, setPreviousPathname] = useState<string | undefined>(pageContext?.urlPathname);

  // Mark initial load as complete after hydration
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInitialLoad(false);
      setPreviousPathname(pageContext?.urlPathname);
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // Complete navigation when page context changes (actual navigation completed)
  // Also scroll to top on page change (but not on initial SSR load)
  useEffect(() => {
    // Skip on initial load - SSR already has the page at the right scroll position
    if (isInitialLoad) return;
    
    // Only scroll if the pathname actually changed (real navigation)
    if (previousPathname === pageContext?.urlPathname) return;

    if (typeof window !== 'undefined') {
      // Use 'auto' instead of 'instant' to prevent scrollbar flash
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    if (navigation) {
      // Small delay to ensure page has rendered
      const timeout = setTimeout(() => {
        navigation.completeNavigation();
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    setPreviousPathname(pageContext?.urlPathname);
  }, [pageContext?.urlPathname, navigation, isInitialLoad, previousPathname]);
  
  // Compute isSearchPage on every render to ensure it updates with client-side navigation
  const isSearchPage = isSearchPageVike(pageContext as any);
  const isSrpIsland = useSrpIsland();
  
  // Get server state from page data if available
  // Memoize to prevent creating new object references on every render
  const serverState = useMemo(() => (pageContext?.data as any)?.serverState, [pageContext?.data]);
  const searchData = useMemo(() => {
    // Use only pageContext.data here. Do not merge `useData()`: during SRP→SRP client navigations,
    // `useData()` can still hold the previous route's payload while `pageContext.data` is briefly empty;
    // merging would push stale `initialFilters` into SearchProvider and overwrite URL-aligned state.
    const data = (pageContext?.data as any) || {};
    const routeSynced = isLayoutClientRouteSyncedWithWindow(pageContext);
    // Avoid false positives: during CSR, window may already show /jobs while urlPathname/data lag one frame.
    const shouldWarnMissingSearchData =
      typeof window !== 'undefined' &&
      isSearchPage &&
      routeSynced &&
      layoutPageDataLacksSrpPayload(data);
    if (shouldWarnMissingSearchData) {
      console.warn('⚠️ [LayoutContent] pageContext.data missing initialQuery/initialResults', {
        hasData: !!pageContext?.data,
        dataKeys: pageContext?.data && typeof pageContext.data === 'object' ? Object.keys(pageContext.data) : [],
        urlPathname: pageContext?.urlPathname,
      });
    }
    return data;
  }, [pageContext, pageContext?.data, isSearchPage]);

  // Use window.location.pathname as fallback on client during hydration so we don't incorrectly
  // get pathname='/' when pageContext isn't ready yet (which would make isSearchPage true and show SRP navbar).
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  const [srpClientVm, setSrpClientVm] = useState<SrpViewModel | null>(null);
  const handleSrpClientViewModelReady = useCallback((vm: SrpViewModel | null) => {
    setSrpClientVm(vm);
  }, []);

  const urlOriginal = (pageContext as { urlOriginal?: string } | undefined)?.urlOriginal;
  /**
   * Full URL key for SRPShell / view-model prefetch cache — must change when query or path changes
   * so we never reuse an Algolia payload for a different search.
   *
   * Do NOT use this to reset `srpClientVm`: clearing merged results on every `pushState` filter update
   * was wiping SearchProvider's `initialResults` and letting URL sync snap back to the bare listing URL.
   */
  const srpVmPrefetchNavigationKey =
    typeof window !== 'undefined' ? window.location.href : (urlOriginal ?? pathname);
  /** Pathname-only: real route changes (SRP ↔ job ↔ another page) without treating filter URL updates as navigation. */
  const srpListingPathIdentity =
    typeof window !== 'undefined' ? window.location.pathname : pathname;

  useEffect(() => {
    setSrpClientVm(null);
  }, [srpListingPathIdentity]);

  const srpMergedInitialResults = srpClientVm?.initialResults ?? searchData.initialResults ?? null;
  const mergedInitialResultsKey = srpMergedInitialResults
    ? `${srpMergedInitialResults.nbHits}-${srpMergedInitialResults.page}-${srpMergedInitialResults.hits?.length || 0}`
    : 'null';
  const initialFiltersKey = JSON.stringify(searchData.initialFilters || {});
  const mergedFacetsData = srpClientVm?.initialFacetsData ?? searchData.facetsData;
  const facetsDataKey = mergedFacetsData ? JSON.stringify(Object.keys(mergedFacetsData).sort()) : 'null';

  const isSlimSrpPayload =
    !searchData.initialResults && (searchData as { useProgressiveSrp?: boolean }).useProgressiveSrp !== false;

  // Memoize SearchProvider props to prevent re-renders when searchData object reference changes
  const searchProviderProps = useMemo(
    () => ({
      indexName: searchData.indexName || getIndexName(locale),
      initialQuery: searchData.initialQuery || '',
      initialFilters: searchData.initialFilters || {},
      initialPage: searchData.initialPage || 0,
      initialResults: srpMergedInitialResults,
      initialSort: searchData.initialSort || 'relevance',
    }),
    [
      searchData.indexName,
      searchData.initialQuery,
      searchData.initialPage,
      mergedInitialResultsKey,
      initialFiltersKey,
      searchData.initialSort,
      locale,
    ],
  );

  // Memoize SearchWrapper props (Step 3: use lite shell on SRP to defer filter bar / mobile nav)
  const searchWrapperProps = useMemo(
    () => ({
      isSearchPage,
      useLiteShell: isSearchPage,
      serverState,
      ssrIsMobile: (pageContext?.data as any)?.ssrIsMobile,
      ssrIsXl: (pageContext?.data as any)?.ssrIsXl,
      initialFacetsData: mergedFacetsData,
    }),
    [isSearchPage, serverState, (pageContext?.data as any)?.ssrIsMobile, (pageContext?.data as any)?.ssrIsXl, facetsDataKey],
  );

  // Static SRP data for Option B shell (no providers on initial paint)
  const staticSRPData = useMemo((): StaticSRPData => {
    const data = pageContext?.data as any;
    return {
      initialResults: srpMergedInitialResults,
      initialQuery: searchData.initialQuery ?? '',
      initialFilters: searchData.initialFilters ?? {},
      initialPage: searchData.initialPage ?? 0,
      indexName: searchData.indexName || getIndexName(locale),
      initialSavedJobIds: srpClientVm?.initialSavedJobIds ?? searchData.initialSavedJobIds,
      initialAppliedJobIds: srpClientVm?.initialAppliedJobIds ?? searchData.initialAppliedJobIds,
      initialViewedJobIds: srpClientVm?.initialViewedJobIds ?? searchData.initialViewedJobIds,
      initialSort: searchData.initialSort,
      initialLayout: searchData.initialLayout,
      facetsData: mergedFacetsData,
      hasNoResults: srpClientVm?.hasNoResults ?? data?.hasNoResults,
      locationTrendsData: srpClientVm?.locationTrendsData ?? data?.locationTrendsData,
      topEmployersStrip:
        srpClientVm?.topEmployersStrip && srpClientVm.topEmployersStrip.length > 0
          ? srpClientVm.topEmployersStrip
          : data?.topEmployersStrip,
    };
  }, [
    pageContext?.data,
    srpMergedInitialResults,
    mergedInitialResultsKey,
    searchData.initialQuery,
    searchData.initialFilters,
    searchData.initialPage,
    searchData.indexName,
    searchData.initialSort,
    searchData.initialLayout,
    srpClientVm,
    locale,
  ]);

  // On client hydration, pageContext.data.srpViewModel may be missing. Read #srp-initial from the DOM
  // synchronously and cache in a ref so we take the same static branch as the server (avoid hydration mismatch).
  // useRef + read in body ensures we see the server-rendered script on the first client render.
  const srpViewModelFromScriptRef = useRef<SrpViewModel | null | undefined>(undefined);
  const prevSrpScriptPathRef = useRef<string>('');
  if (typeof window !== 'undefined') {
    const pathNow = window.location.pathname;
    if (prevSrpScriptPathRef.current !== pathNow) {
      prevSrpScriptPathRef.current = pathNow;
      srpViewModelFromScriptRef.current = undefined;
    }
    if (srpViewModelFromScriptRef.current === undefined) {
      const el = document.getElementById('srp-initial');
      if (el && el.tagName === 'SCRIPT') {
        srpViewModelFromScriptRef.current = parseSrpInitialScript((el as HTMLScriptElement).textContent ?? '');
      } else {
        srpViewModelFromScriptRef.current = null;
      }
    }
  }
  const fromWindow =
    typeof window !== 'undefined' ? (window as any).__SRP_VIEW_MODEL__ as SrpViewModel | null | undefined : null;
  const clientSrpViewModel =
    typeof window !== 'undefined' ? srpViewModelFromScriptRef.current ?? fromWindow ?? null : null;

  const srpViewModel = (searchData as any).srpViewModel ?? clientSrpViewModel;
  // Only use for static render when we have a full view model (not the _fetch bootstrap).
  const fullSrpViewModel: SrpViewModel | null =
    srpViewModel &&
    typeof srpViewModel === 'object' &&
    !('_fetch' in srpViewModel && (srpViewModel as any)._fetch === true) &&
    typeof (srpViewModel as SrpViewModel).locale === 'string' &&
    Array.isArray((srpViewModel as SrpViewModel).jobs)
      ? (srpViewModel as SrpViewModel)
      : null;

  if (typeof window !== 'undefined' && (window as any).__SRP_DEBUG_LAYOUT__) {
    console.log('[LayoutContent] branch', {
      isSearchPage,
      isSrpIsland,
      hasSrpViewModel: !!srpViewModel,
      hasFullSrpViewModel: !!fullSrpViewModel,
    });
  }

  // Job detail: static shell then client swap (like SRP). Never render SearchWrapper+JobDetailPage for job route (avoids hydration mismatch).
  // Match both path formats: with locale (/en/job/...) and without (urlLogical: /job/...).
  const jobDetailWithLocale = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/|$)/;
  const jobDetailNoLocale = /^\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/|$)/;
  const pathForJob = String(pathname || '');
  const isJobPage = jobDetailWithLocale.test(pathForJob) || jobDetailNoLocale.test(pathForJob);

  // Job data for hydration is read synchronously from `#job-initial` inside JobRouteContent so the first
  // client paint matches SSR (no empty shell / null Suspense fallback for crawlers).
  if (isJobPage) {
    return <JobRouteContent footerStrings={footerStrings} />;
  }

  // Progressive SRP takeover (no hydration): server-only static HTML + empty container + snapshot.
  // Backoffice: main site navbar + content (no SearchWrapper). Uses MinimalChrome so navbar is visible.
  const isBackofficeRoute = /\/backoffice\//.test(pathname || '');
  if (isBackofficeRoute) {
    return <MinimalChrome noPadding>{children}</MinimalChrome>;
  }

  // Tools pages (applied-jobs, saved-jobs, viewed-jobs, activity Sankey route, etc.): navbar + content via MinimalChrome
  // so page content renders in the main area without being wrapped in SearchWrapper (which can leave content blank).
  if (isToolsPage(pathname)) {
    return (
      <FooterStringsProvider strings={footerStrings}>
        <MinimalChrome>{children}</MinimalChrome>
      </FooterStringsProvider>
    );
  }

  // Client mounts into #srp-app (overlaid on #srp-ssr so only one contributes to layout, avoiding CLS/scrollbar),
  // then removes #srp-ssr and shows #srp-app (see srp.client.ts).
  // We need fullSrpViewModel for the static shell; when script only has _fetch, island fetches from /api/srp-state.
  if (isSearchPage && fullSrpViewModel && !isSrpIsland) {
    return (
      <>
        <div id="srp-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
          <div id="srp-ssr">
            {/* Same outer wrapper as SearchWrapper so static and hydrated layout match (reduces CLS on swap). */}
            <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#170F07] w-full max-w-full">
              <SrpView model={fullSrpViewModel} mode="static" />
            </div>
          </div>
          <div
            id="srp-app"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              visibility: 'hidden',
            }}
            aria-hidden="true"
          />
        </div>
        <script
          id="srp-initial"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: serializeForHtmlScript({ _fetch: true } as any),
          }}
        />
      </>
    );
  }

  return (
    <>
      <ErrorBoundary fallback={null}>
        <ProfilePreferencesSettingsSyncGuard userId={effectiveUserId} />
      </ErrorBoundary>
      <IntlProvider 
        messages={messages} 
        locale={locale} 
        defaultLocale={locale}
        onError={(err) => {
          // Suppress MISSING_TRANSLATIONS errors
          if (err.code === 'MISSING_TRANSLATION') {
            return;
          }
          // Log other errors
          console.error('[react-intl] error:', err);
        }}
      >
        <FooterStringsProvider strings={footerStrings}>
        <LocaleMismatchPrompt />
        {isSearchPage ? (
        <SRPShell
          data={staticSRPData}
          locale={locale}
          pathname={pathname}
          isSlimPayload={isSlimSrpPayload}
          navigationKey={srpVmPrefetchNavigationKey}
          onClientViewModelReady={handleSrpClientViewModelReady}
          interactiveTree={
            <SavedStateContextProviderMemoizedMemo
              savedJobIds={srpClientVm?.initialSavedJobIds ?? searchData.initialSavedJobIds}
              appliedJobIds={srpClientVm?.initialAppliedJobIds ?? searchData.initialAppliedJobIds}
              viewedJobIds={srpClientVm?.initialViewedJobIds ?? searchData.initialViewedJobIds}
            >
              <SearchProvider
                indexName={searchProviderProps.indexName}
                initialQuery={searchProviderProps.initialQuery}
                initialFilters={searchProviderProps.initialFilters}
                initialPage={searchProviderProps.initialPage}
                initialResults={searchProviderProps.initialResults}
                initialSort={searchProviderProps.initialSort}
                initialViewMode={searchData.initialLayout === 'map' ? 'map' : 'list'}
                syncToUrl={true}
              >
                <SearchWrapper {...searchWrapperProps}>
                  {children}
                </SearchWrapper>
              </SearchProvider>
            </SavedStateContextProviderMemoizedMemo>
          }
        />
      ) : isMinimalRoute(pathname) ? (
        <MinimalChrome noPadding>{children}</MinimalChrome>
      ) : (
        <SearchProvider
          indexName={getIndexName(locale)}
          initialQuery={''}
          initialFilters={{}}
          initialPage={0}
          initialResults={null}
          initialSort={'relevance'}
          syncToUrl={false}
        >
          <SearchWrapper 
            isSearchPage={isSearchPage} 
            serverState={serverState}
            initialFacetsData={searchData.facetsData}
          >
            {children}
          </SearchWrapper>
        </SearchProvider>
      )}
        </FooterStringsProvider>
      </IntlProvider>
    </>
  );
}

/** Wraps layout in IntlProvider so Page (and any child) always has intl during hydration. */
function LayoutDefaultWithIntl({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const pathnameForLocale =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = resolvePageLocale({
    locale: pageContext?.locale as string | undefined,
    urlPathname: pathnameForLocale,
  });
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const initialUser = (pageContext?.user ?? null) as SessionUser | null;

  useLayoutEffect(() => {
    syncHtmlLangAttribute(locale);
  }, [locale]);

  return (
    <IntlProvider
      messages={messages}
      locale={locale}
      defaultLocale={locale}
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') return;
        console.error('[react-intl] error:', err);
      }}
    >
      <AuthProvider initialUser={initialUser}>
        <FeedbackModalProvider>
          <NavigationProvider>
            <ToastProvider>
              <NavigationProgress />
              <ToastContainer />
              <ErrorBoundary fallback={<LayoutPageErrorFallback />}>
                <InspectorMount>
                  <LayoutContent>{children}</LayoutContent>
                </InspectorMount>
              </ErrorBoundary>
              <CookieConsentBanner />
            </ToastProvider>
          </NavigationProvider>
        </FeedbackModalProvider>
      </AuthProvider>
    </IntlProvider>
  );
}

/**
 * Routes that use StaticPageLayout (or any page-level layout that opts out of the
 * full provider tree). They are returned as pure server-rendered HTML — no IntlProvider,
 * AuthProvider, SearchWrapper, or other heavy providers run for these paths.
 * Interactive features are handled by small inline script modules in the page itself.
 */
const STATIC_ROUTE_RE = /^\/(en|fi|sv)?$/;

/**
 * Resource content routes that use ResourcePageLayout, which provides its own
 * navbar (ResourceNavbar). LayoutDefault must not wrap these in MinimalChrome
 * (which also renders a NavigationNavbar) — doing so would produce two headers.
 */
const RESOURCE_ROUTE_RE =
  /^\/(en\/resources|fi\/tyoelama|sv\/arbetsliv)(\/|$)|^\/(resources|tyoelama|arbetsliv)(\/|$)/;

const JOB_SEARCH_GUIDE_ROUTE_RE =
  /^\/(en\/job-search|fi\/tyonhaku|sv\/jobbsok)(\/|$)|^\/(job-search|tyonhaku|jobbsok)(\/|$)/;

function isStaticRoute(pathname: string): boolean {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  return STATIC_ROUTE_RE.test(p);
}

function isResourceRoute(pathname: string): boolean {
  const p = String(pathname || '');
  return RESOURCE_ROUTE_RE.test(p) || JOB_SEARCH_GUIDE_ROUTE_RE.test(p);
}

export default function LayoutDefault({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  if (isStaticRoute(pathname) || isResourceRoute(pathname)) {
    // These pages provide their own layout/chrome via their page-level Layout
    // config (LandingPageLayout / ResourcePageLayout) — bypass all providers.
    return <>{children}</>;
  }

  return <LayoutDefaultWithIntl>{children}</LayoutDefaultWithIntl>;
}
